import { FormikErrors, FormikTouched } from "formik";
import { ChangeEvent, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CountryData } from "react-phone-input-2";
import {
  Formik,
  FormikInsurance,
  FormikContractorNatural,
  FormikInsured,
  InputSelectOption,
  Gender,
  Nationality,
} from "src/types";
import {
  useCapOptions,
  useContractorIsInsuredQuestionId,
  useFcQuestionOptions,
  useGenderOptions,
  useMunicipalitiesOptions,
  useNationalityOptions,
  useProvincesOptions,
} from "../../../../handlers/form-label-mapping.hook";
import { useDebouncedEffect } from "src/utils";
import { defaultSelectInputOptionValue } from "src/storage";

export function useNaturalContractor({
  formik,
}: {
  formik: Formik<FormikInsurance>;
}) {
  const { t } = useTranslation();

  const {
    setFieldValue,
    setFieldError,
    setFieldTouched,
    values: { contractor, insuredList },
  } = formik;

  const naturalContractor = contractor as FormikContractorNatural;
  const naturalTouched =
    (formik.touched.contractor as FormikTouched<FormikContractorNatural>) || {};
  const naturalErrors =
    (formik.errors.contractor as FormikErrors<FormikContractorNatural>) || {};

  const { nationalityOptions } = useNationalityOptions();

  const { fcQuestionOptions } = useFcQuestionOptions();

  const { genderOptions } = useGenderOptions();

  const { contractorIsInsuredQuestionOptions } =
    useContractorIsInsuredQuestionId();

  const onChangePhone = (
    value: string,
    data: CountryData,
    event: ChangeEvent<HTMLInputElement>,
    formattedValue: string
  ) => {
    setFieldValue("contractor.contractorPhone", {
      country: data,
      formattedPhone:
        value === (data as CountryData)?.dialCode ? value : formattedValue,
      value: formattedValue,
    });
  };

  // ContractorFcQuestion  =============================================
  // Handles logic when the user switches the FC question.
  // Reset error and touched object
  const onSelectFcQuestion = (event: ChangeEvent<HTMLInputElement>) => {
    const contractorFcQuestion = event.target.value;
    if (
      !contractorFcQuestion ||
      contractorFcQuestion === naturalContractor.contractorFcQuestion
    )
      return;

    const resetFields = [
      "contractor.contractorBirth",
      "contractor.contractorBirthNation",
      "contractor.contractorBirthPlace",
      "contractor.contractorBirthProvince",
      "contractor.contractorGender",
      "contractor.contractorFc",
    ];

    resetFields.forEach((field) => {
      setFieldValue(field, "");
      setFieldError(field, "");
      setFieldTouched(field, false, false);
    });

    setFieldValue("contractor.contractorGender", Gender.M);
  };

  useDebouncedEffect(
    () => {
      const idx = insuredList.findIndex((i) => i.id === "contractor");
      if (idx === -1) return;

      const updates: Array<[keyof FormikInsured, any, any]> = [
        [
          "insuredName",
          naturalContractor.contractorName,
          insuredList[idx]?.insuredName,
        ],
        [
          "insuredSurname",
          naturalContractor.contractorSurname,
          insuredList[idx]?.insuredSurname,
        ],
        [
          "insuredBirth",
          naturalContractor.contractorBirth,
          insuredList[idx]?.insuredBirth,
        ],
        [
          "insuredBirthNation",
          naturalContractor.contractorBirthNation,
          insuredList[idx]?.insuredBirthNation,
        ],
        [
          "insuredBirthPlace",
          naturalContractor.contractorBirthPlace,
          insuredList[idx]?.insuredBirthPlace,
        ],
        [
          "insuredGender",
          naturalContractor.contractorGender,
          insuredList[idx]?.insuredGender,
        ],
        [
          "insuredNationality",
          naturalContractor.contractorNationality,
          insuredList[idx]?.insuredNationality,
        ],
        [
          "insuredFc",
          naturalContractor.contractorFc,
          insuredList[idx]?.insuredFc,
        ],
        [
          "insuredFcQuestion",
          naturalContractor.contractorFcQuestion,
          insuredList[idx]?.insuredFcQuestion,
        ],
      ];

      updates.forEach(([field, newValue, currentValue]) => {
        if (newValue !== currentValue) {
          setFieldValue(`insuredList.${idx}.${field}`, newValue);
        }
      });
    },
    [
      naturalContractor.contractorName,
      naturalContractor.contractorSurname,
      naturalContractor.contractorBirth,
      naturalContractor.contractorBirthNation,
      naturalContractor.contractorBirthPlace,

      naturalContractor.contractorBirthProvince,
      naturalContractor.contractorGender,
      naturalContractor.contractorNationality,
      naturalContractor.contractorFc,
      naturalContractor.contractorFcQuestion,
    ],
    500
  );

  // Viario  =============================================
  const { provincesOptions } = useProvincesOptions();
  const { municipalitiesOptions: residenceMunicipalitiesOptions } =
    useMunicipalitiesOptions(naturalContractor.contractorResidenceProvince.id);
  const { capOptions } = useCapOptions(
    naturalContractor.contractorResidenceProvince.id,
    naturalContractor.contractorResidenceMunicipality.id,
  );

  const onSelectResidenceProvince = (
    event: ChangeEvent<{ name: string; value: InputSelectOption }>
  ) => {
    setFieldTouched(
      "contractor.contractorResidenceMunicipality.value",
      true,
      true
    );
    setFieldValue(
      "contractor.contractorResidenceMunicipality",
      { ...defaultSelectInputOptionValue },
      true
    );

    setFieldTouched("contractor.contractorCap.value", true, true);
    setFieldValue(
      "contractor.contractorCap",
      { ...defaultSelectInputOptionValue },
      true
    );

    let province = event.target.value;

    if (!province?.id || !province?.value)
      province = { ...defaultSelectInputOptionValue };

    setFieldValue("contractor.contractorResidenceProvince", province, false);
    setFieldTouched(
      "contractor.contractorResidenceProvince.value",
      false,
      false
    );
  };

  const onSelectResidenceMunicipality = (
    event: ChangeEvent<{ name: string; value: InputSelectOption }>
  ) => {
    setFieldTouched("contractor.contractorCap.value", true, true);
    setFieldValue(
      "contractor.contractorCap",
      { ...defaultSelectInputOptionValue },
      true
    );

    let municipality = event.target.value;

    if (!municipality?.id || !municipality?.value)
      municipality = { ...defaultSelectInputOptionValue };

    setFieldValue(
      "contractor.contractorResidenceMunicipality",
      municipality,
      false
    );
    setFieldTouched(
      "contractor.contractorResidenceMunicipality.value",
      false,
      false
    );
  };

  const onSelectContractorCap = (
    event: ChangeEvent<{ name: string; value: InputSelectOption }>
  ) => {
    let cap = event.target.value;

    if (!cap?.id || !cap?.value) cap = { ...defaultSelectInputOptionValue };

    setFieldValue("contractor.contractorCap", cap, false);
    setFieldTouched("contractor.contractorCap.value", true, false);
  };

  useEffect(() => {
    if (capOptions.length !== 1) return;

    const cap = capOptions[0];

    setFieldValue("contractor.contractorCap", cap, false);
    setFieldTouched("contractor.contractorCap.value", false, false);
  }, [capOptions]);

  return {
    naturalContractor,
    naturalTouched,
    naturalErrors,
    nationalityOptions,
    fcQuestionOptions,
    genderOptions,
    contractorIsInsuredQuestionOptions,
    provincesOptions,
    residenceMunicipalitiesOptions,
    capOptions,
    t,
    onChangePhone,
    onSelectFcQuestion,
    onSelectResidenceProvince,
    onSelectResidenceMunicipality,
    onSelectContractorCap,
  };
}
